
import { ArticleData } from "@/data/articles/types";
import { loadContentFromSupabase } from "@/utils/storageLoader";
import { isProductionBuild } from "@/utils/productionFallbacks";

/**
 * Centralized service for loading and processing article content
 * Prioritizes Supabase loading while maintaining backward compatibility
 */
export class ArticleContentService {
  /**
   * Load article content from the appropriate source
   * @param article The article metadata object
   * @returns Promise containing the article content or undefined
   */
  public static async loadContent(article: ArticleData): Promise<string | undefined> {
    console.log(`ArticleContentService: Loading content for article: ${article.slug}`);
    
    // If article already has content, return it directly
    if (article.content) {
      console.log(`ArticleContentService: Article ${article.slug} already has inline content`);
      return article.content;
    }
    
    // First attempt: Try loading from Supabase storage
    try {
      console.log(`ArticleContentService: Attempting to load from Supabase storage for ${article.slug}`);
      const supabaseContent = await this.loadFromSupabase(article);
      
      if (supabaseContent) {
        console.log(`ArticleContentService: Successfully loaded content from Supabase for ${article.slug}`);
        return supabaseContent;
      }
    } catch (error) {
      console.error(`ArticleContentService: Error loading from Supabase: ${error}`);
      // Continue to fallback methods
    }
    
    // Fallback: Try legacy loading methods
    return await this.loadUsingLegacyMethods(article);
  }
  
  /**
   * Load content from Supabase storage
   */
  private static async loadFromSupabase(article: ArticleData): Promise<string | undefined> {
    // Exit early if article doesn't have a slug
    if (!article.slug) {
      console.warn('ArticleContentService: Cannot load from Supabase without a slug');
      return undefined;
    }
    
    try {
      // Try to load from Supabase with just the slug
      const content = await loadContentFromSupabase(`articles/${article.slug}.md`);
      if (content) return content;
      
      // Try HTML format if markdown failed
      const htmlContent = await loadContentFromSupabase(`articles/${article.slug}.html`);
      if (htmlContent) return htmlContent;
      
      // Try using contentPath if direct slug approach failed
      if (article.contentPath) {
        const pathContent = await loadContentFromSupabase(article.contentPath);
        if (pathContent) return pathContent;
      }
      
      return undefined;
    } catch (error) {
      console.error(`ArticleContentService: Supabase loading error for ${article.slug}: ${error}`);
      return undefined;
    }
  }
  
  /**
   * Fallback to legacy content loading methods
   */
  private static async loadUsingLegacyMethods(article: ArticleData): Promise<string | undefined> {
    // Don't proceed without a contentPath
    if (!article.contentPath) {
      console.warn(`ArticleContentService: No contentPath defined for article: ${article.slug}`);
      return undefined;
    }
    
    console.log(`ArticleContentService: Falling back to legacy loading for ${article.slug}`);
    
    // Special handling for user behaviour article
    if (article.slug === 'user-behaviour-sequence-modelling') {
      return await this.loadUserBehaviourContent();
    }
    
    // Generic content loading with production fallbacks
    return await this.loadFromFilePath(article.contentPath, article.slug);
  }
  
  /**
   * Load content from file path (legacy method)
   */
  private static async loadFromFilePath(contentPath: string, slug: string): Promise<string | undefined> {
    try {
      const isProduction = isProductionBuild();
      console.log(`ArticleContentService: Loading from path ${contentPath} for ${slug} in ${isProduction ? 'PRODUCTION' : 'development'}`);
      
      // First try the direct import approach for Vite
      try {
        const contentModule = await import(`../article-content/${contentPath}`);
        if (contentModule.default) {
          console.log(`ArticleContentService: Successfully loaded content via import for: ${contentPath}`);
          return contentModule.default;
        }
      } catch (importError) {
        console.log(`ArticleContentService: Import approach failed, trying fetch: ${importError}`);
      }
      
      // Use multiple fetch attempts with different paths as fallbacks
      const possiblePaths = [
        `/src/data/${contentPath}`,
        `src/data/${contentPath}`,
        `/${contentPath}`,
        `${contentPath}`
      ];
      
      // Add production-specific paths
      if (isProduction) {
        possiblePaths.unshift(
          `/assets/${contentPath.split('/').pop()}`,
          `/html-content/${contentPath.split('/').pop()}`
        );
      }
      
      for (const path of possiblePaths) {
        try {
          console.log(`ArticleContentService: Trying fetch path: ${path}`);
          const contentResponse = await fetch(path);
          
          if (contentResponse.ok) {
            const content = await contentResponse.text();
            console.log(`ArticleContentService: Successfully loaded content from path: ${path}, length: ${content.length}`);
            return content;
          }
        } catch (e) {
          console.log(`ArticleContentService: Path failed: ${path}`);
        }
      }
      
      console.error(`ArticleContentService: Failed to load content from any path for: ${contentPath}`);
      
      if (isProduction) {
        console.log(`ArticleContentService: PRODUCTION - Providing default fallback content for ${slug}`);
        return this.getFallbackContent(slug);
      }
      
      return undefined;
    } catch (error) {
      console.error(`ArticleContentService: Error in legacy loading: ${error}`);
      return undefined;
    }
  }
  
  /**
   * Special handler for user behaviour sequence article
   */
  private static async loadUserBehaviourContent(): Promise<string | undefined> {
    console.log('ArticleContentService: Loading user-behaviour-sequence-modelling HTML content');
    
    // Check for production environment
    const isProduction = isProductionBuild();
    
    try {
      // For production, try direct asset paths first
      if (isProduction) {
        console.log('ArticleContentService: PRODUCTION - Trying direct asset paths for user-behaviour');
        
        const productionPaths = [
          '/assets/InsessionadaptationviaUserbehaviourSequencemo.html',
          '/html-content/InsessionadaptationviaUserbehaviourSequencemo.html'
        ];
        
        for (const path of productionPaths) {
          try {
            const response = await fetch(path);
            if (response.ok) {
              const content = await response.text();
              console.log(`ArticleContentService: Successfully loaded HTML from production path: ${path}`);
              return content;
            }
          } catch (e) {
            console.log(`ArticleContentService: Production path failed: ${path}`);
          }
        }
      }
    
      // Regular development path
      const response = await fetch('/src/data/html-content/InsessionadaptationviaUserbehaviourSequencemo.html');
      if (response.ok) {
        const content = await response.text();
        console.log(`ArticleContentService: Successfully loaded HTML content for user-behaviour-sequence-modelling, length: ${content.length}`);
        return content;
      } else {
        console.error(`ArticleContentService: Failed to fetch HTML content: ${response.status}`);
      }
    } catch (error) {
      console.error('ArticleContentService: Error loading HTML content:', error);
    }
  
    // If the first attempt failed, try with a different path format
    try {
      const response = await fetch('src/data/html-content/InsessionadaptationviaUserbehaviourSequencemo.html');
      if (response.ok) {
        const content = await response.text();
        console.log(`ArticleContentService: Successfully loaded HTML content from alt path, length: ${content.length}`);
        return content;
      }
    } catch (error) {
      console.error('ArticleContentService: Error loading HTML content from alt path:', error);
    }
    
    // Return fallback content in production
    if (isProduction) {
      return this.getFallbackContent('user-behaviour-sequence-modelling');
    }
    
    return undefined;
  }
  
  /**
   * Provides fallback content for production when content loading fails
   */
  private static getFallbackContent(slug: string): string {
    return `
      <div class="p-6 bg-amber-50 border border-amber-200 rounded-lg">
        <h2 class="text-2xl font-semibold mb-3">Article Content</h2>
        <p class="mb-4">We're currently experiencing technical difficulties loading this article. 
        Please check back later or try refreshing the page.</p>
        <p class="text-sm text-gray-600">Article ID: ${slug}</p>
      </div>
    `;
  }
}
